<template>
  <div
    v-if="showIcon"
    ref="fieldRoot"
    class="barcode-target-wrap"
    :class="wrapperClass"
  >
    <b-input-group class="barcode-target-field input-group-merge">
      <slot />
      <b-input-group-append is-text>
        <span
          class="barcode-target-icon"
          title="Barcode scanner supported"
          aria-label="Barcode scanner supported"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M3 5v14" />
            <path d="M8 5v14" />
            <path d="M12 5v14" />
            <path d="M17 5v14" />
            <path d="M21 5v14" />
          </svg>
        </span>
      </b-input-group-append>
    </b-input-group>

    <div
      v-if="hintVisible"
      class="barcode-target-hint"
      :class="{ 'barcode-target-hint--fade-out': hintFading }"
      role="status"
    >
      <span class="barcode-target-hint__text">Use barcode scanner here</span>
      <button
        type="button"
        class="barcode-target-hint__close"
        aria-label="Dismiss hint"
        @click="dismissHint"
      >
        ×
      </button>
    </div>
  </div>
  <div
    v-else
    :class="wrapperClass"
  >
    <slot />
  </div>
</template>

<script>
import { BInputGroup, BInputGroupAppend } from 'bootstrap-vue'

const HINT_HOLD_MS = 5000
const HINT_FADE_MS = 3000

export default {
  name: 'BarcodeTargetField',
  components: {
    BInputGroup,
    BInputGroupAppend,
  },
  props: {
    showIcon: {
      type: Boolean,
      default: true,
    },
    wrapperClass: {
      type: [String, Array, Object],
      default: '',
    },
    hintKey: {
      type: String,
      default: 'default',
    },
    active: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      hintVisible: false,
      hintFading: false,
      hintTimer: null,
      hintFadeTimer: null,
      visibilityObserver: null,
      onModalShown: null,
      onModalHidden: null,
    }
  },
  watch: {
    showIcon(value) {
      if (value) {
        this.$nextTick(() => this.setupVisibilityWatcher())
      } else {
        this.teardownVisibilityWatcher()
        this.hideHint()
      }
    },
    active(value) {
      if (value) {
        this.$nextTick(() => {
          if (this.isFieldVisible()) {
            this.showHint()
          }
        })
      } else {
        this.hideHint()
      }
    },
  },
  mounted() {
    this.setupVisibilityWatcher()
  },
  beforeUnmount() {
    this.teardownVisibilityWatcher()
    this.clearHintTimer()
  },
  methods: {
    clearHintTimer() {
      if (this.hintTimer) {
        clearTimeout(this.hintTimer)
        this.hintTimer = null
      }
      if (this.hintFadeTimer) {
        clearTimeout(this.hintFadeTimer)
        this.hintFadeTimer = null
      }
    },
    getRootEl() {
      return this.$refs.fieldRoot || null
    },
    isFieldVisible() {
      const el = this.getRootEl()
      if (!el || !this.showIcon) {
        return false
      }

      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false
      }

      const rect = el.getBoundingClientRect()
      if (rect.width < 2 || rect.height < 2) {
        return false
      }

      const inModal = el.closest('.modal')
      if (inModal && !inModal.classList.contains('show')) {
        return false
      }

      if (!this.active) {
        return false
      }

      return true
    },
    setupVisibilityWatcher() {
      this.teardownVisibilityWatcher()
      if (!this.showIcon) {
        return
      }

      const el = this.getRootEl()
      if (!el) {
        return
      }

      if (typeof IntersectionObserver === 'function') {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[0]
          if (entry?.isIntersecting && this.isFieldVisible()) {
            this.showHint()
          }
        }, { threshold: 0.2 })
        this.visibilityObserver.observe(el)
      }

      this.onModalShown = () => {
        this.$nextTick(() => {
          if (this.isFieldVisible()) {
            this.showHint()
          }
        })
      }
      this.onModalHidden = () => {
        this.hideHint()
      }
      document.addEventListener('shown.bs.modal', this.onModalShown)
      document.addEventListener('hidden.bs.modal', this.onModalHidden)

      this.$nextTick(() => {
        if (this.isFieldVisible()) {
          this.showHint()
        }
      })
      window.setTimeout(() => {
        if (this.isFieldVisible()) {
          this.showHint()
        }
      }, 350)
    },
    teardownVisibilityWatcher() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect()
        this.visibilityObserver = null
      }
      if (this.onModalShown) {
        document.removeEventListener('shown.bs.modal', this.onModalShown)
        this.onModalShown = null
      }
      if (this.onModalHidden) {
        document.removeEventListener('hidden.bs.modal', this.onModalHidden)
        this.onModalHidden = null
      }
    },
    showHint() {
      if (!this.showIcon || !this.isFieldVisible()) {
        return
      }

      this.clearHintTimer()
      this.hintVisible = true
      this.hintFading = false

      this.hintFadeTimer = setTimeout(() => {
        this.hintFading = true
      }, HINT_HOLD_MS)

      this.hintTimer = setTimeout(() => {
        this.hideHint()
      }, HINT_HOLD_MS + HINT_FADE_MS)
    },
    hideHint() {
      this.clearHintTimer()
      this.hintVisible = false
      this.hintFading = false
    },
    dismissHint() {
      this.hideHint()
    },
  },
}
</script>

<style scoped>
.barcode-target-wrap {
  position: relative;
  width: 100%;
}

.barcode-target-field {
  width: 100%;
}

.barcode-target-icon {
  display: inline-flex;
  align-items: center;
  color: #6e6b7b;
  line-height: 1;
}

.barcode-target-hint {
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  left: auto;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: min(100%, 20rem);
  padding: 0.4rem 0.55rem;
  border-radius: 0.4rem;
  background: #1f8a4c;
  color: #fff;
  font-size: 0.78rem;
  line-height: 1.25;
  box-shadow: 0 8px 20px rgba(31, 138, 76, 0.22);
  opacity: 1;
  pointer-events: auto;
  transition: opacity 3s ease;
}

.barcode-target-hint--fade-out {
  opacity: 0;
  pointer-events: none;
}

.barcode-target-hint__close {
  flex-shrink: 0;
  width: 1.2rem;
  height: 1.2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
}

.barcode-target-hint__close:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
